import React from "react";
import styled from "styled-components";
import Tooltip from "@mui/material/Tooltip";
import { TextEntryFormControl } from "../TextEntryFormControl";
import { TextControlStyle } from "../../Utilities/FormDesignConstants";
import { StyledFormHelperText, StyledTextField } from "./CommonStyled";
import { IssueData } from "../../Utilities/ControlRenderContext";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { StringDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { UILabel } from "../../../CommonUI/BuildlingBlocks/UILabel";

//@RegisterUIControl('Data (Entry)', 'Text Field', ControlType.Control, <TextFieldIcon />)
export class TextFormControl extends TextEntryFormControl {
    readonly __type: string = "Text Field";

    render() {
        let style = this.getFormContext()?.form?.defaultTextStyle;

        if (this.overrideStyle && this.style) style = this.style;

        let size: "medium" | "small" = this.getFormContext()?.form?.defaultTextSize ?? "medium";

        if (this.overrideStyle && this.size) size = this.size;

        const issueData: IssueData | null = this.getFormContext()!.getControlContext(this)!.getRuntimeIssueData();
        const issueText: string = issueData?.text || "";
        const customWidth = this.fullWidth ? "100%" : this.width ? `${this.width}px` : "200px";

        switch (style) {
            case TextControlStyle.LABELED:
                return (
                    <StyledTextFieldWrap width={customWidth}>
                        {this.label && <UILabel variant={"form_label"}>{this.label}</UILabel>}
                        <StyledTextField
                            size={size}
                            data-size={size}
                            data-testid={this.valueName}
                            variant={"outlined"}
                            fullWidth={this.fullWidth}
                            width={customWidth}
                            value={this.valueName ? this.getFormDataValue(this.valueName) || "" : ""}
                            onChange={this.handleChange}
                            error={Boolean(issueText)}
                            InputProps={{
                                inputRef: this.ref,
                            }}
                        />
                        {issueText && issueText.length > 30 && (
                            <Tooltip
                                title={issueText}
                                arrow
                                placement="bottom"
                            >
                                <StyledFormHelperText
                                    error={Boolean(issueText)}
                                    data-testid={`${this.valueName || "textfield"}-hinttext`}
                                >
                                    {issueText.slice(0, 30) + "..."}
                                </StyledFormHelperText>
                            </Tooltip>
                        )}
                        {((issueText && issueText.length < 31) || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "textfield"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </StyledTextFieldWrap>
                );
            case TextControlStyle.SHADED:
                return (
                    <StyledTextFieldWrap width={customWidth}>
                        <StyledTextField
                            size={size}
                            data-size={size}
                            data-testid={this.valueName}
                            variant={"filled"}
                            customstyle={"filled"}
                            fullWidth={this.fullWidth}
                            width={customWidth}
                            value={this.valueName ? this.getFormDataValue(this.valueName) || "" : ""}
                            onChange={this.handleChange}
                            error={Boolean(issueText)}
                            label={this.label ?? this.valueName}
                            InputProps={{
                                inputRef: this.ref,
                            }}
                        />
                        {(issueText || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "textfield"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </StyledTextFieldWrap>
                );
            case TextControlStyle.UNDERLINED:
                return (
                    <StyledTextFieldWrap width={customWidth}>
                        <StyledTextField
                            size={size}
                            data-size={size}
                            data-testid={this.valueName}
                            variant={"standard"}
                            customstyle={"standard"}
                            fullWidth={this.fullWidth}
                            width={customWidth}
                            value={this.valueName ? this.getFormDataValue(this.valueName) || "" : ""}
                            onChange={this.handleChange}
                            error={Boolean(issueText)}
                            label={this.label ?? this.valueName}
                            InputProps={{
                                inputRef: this.ref,
                            }}
                        />
                        {(issueText || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "textfield"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </StyledTextFieldWrap>
                );
            case TextControlStyle.OUTLINE:
                return (
                    <StyledTextFieldWrap width={customWidth}>
                        <StyledTextField
                            size={size}
                            data-size={size}
                            data-testid={this.valueName}
                            variant={"outlined"}
                            customstyle={"outlined"}
                            fullWidth={this.fullWidth}
                            width={customWidth}
                            value={this.valueName ? this.getFormDataValue(this.valueName) || "" : ""}
                            onChange={this.handleChange}
                            error={Boolean(issueText)}
                            label={this.label ?? this.valueName}
                            InputProps={{
                                inputRef: this.ref,
                            }}
                        />
                        {(issueText || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "textfield"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </StyledTextFieldWrap>
                );
        }
    }

    renderEditUI(): JSX.Element | null {
        return <XTextFieldEditUI editMe={this} />;
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        if (!this.valueName) return;

        let def = new StringDataDefinition();
        def.name = this.valueName;
        return def;
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(this, retData, "__type");

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(this, data, "__type");
    }

    private handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (this.valueName) {
            this.setFormDataValue(this.valueName, event.currentTarget.value);
        }
    };
}

function XTextFieldEditUI(props: { editMe: TextFormControl }) {
    return (
        <UISpacedBoxContent>
            {props.editMe.renderBaseDataControlEditor()}
            {props.editMe.renderTextControlBasePropertyEditor()}
            {props.editMe.renderTextStyleSelectionEditor()}
        </UISpacedBoxContent>
    );
}

const StyledTextFieldWrap = styled.div<{ width?: string }>`
    width: ${({ width }) => width} !important;
    height: 100% !important;
    box-sizing: border-box !important;
    position: relative !important;
`;
